// Minimal RFC 5545 .ics builder for meeting invites (Sales Calendar CP4).
// Hand-built (no dep): METHOD:REQUEST VEVENT with organizer + attendees, so
// attendees can add the meeting to their calendar and (client permitting) RSVP.
// CANCEL on meeting cancellation is CP4b.

export interface IcsMeeting {
  id: string
  title: string
  start_time: string   // ISO
  end_time: string     // ISO
  location?: string | null
  notes?: string | null
  attendee_emails?: string[]
}

export interface IcsOptions {
  organizerName: string
  organizerEmail: string
  link?: string   // "Open in Orkestra" URL for the DESCRIPTION
}

// RFC 5545 text escaping: backslash, semicolon, comma, and newlines.
export function escIcsText(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n')
}

// UTC "basic" date-time: YYYYMMDDTHHMMSSZ.
export function toIcsDateUtc(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}T${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`
}

// Content-line folding: lines > 75 octets are folded with CRLF + a leading
// space on continuation lines (char-based; ASCII assumption for the fold width).
export function foldIcsLine(line: string): string {
  if (line.length <= 75) return line
  const parts: string[] = [line.slice(0, 75)]
  let rest = line.slice(75)
  while (rest.length > 74) { parts.push(' ' + rest.slice(0, 74)); rest = rest.slice(74) }
  if (rest.length) parts.push(' ' + rest)
  return parts.join('\r\n')
}

/**
 * Build a METHOD:REQUEST .ics for a meeting invite.
 * SEQUENCE = floor(now/1000) (seconds since epoch): a 32-bit-safe monotonic-
 * per-send value (fits until 2038) so a later send in a different second
 * supersedes an earlier one on the recipient's calendar — avoids adding a
 * schema column. Same-second re-sends share a SEQUENCE (acceptable for CP4).
 */
export function buildMeetingIcs(m: IcsMeeting, opts: IcsOptions, nowMs: number = Date.now()): string {
  const seq = Math.floor(nowMs / 1000)
  const descParts: string[] = []
  if (m.notes && m.notes.trim()) descParts.push(m.notes.trim())
  if (opts.link) descParts.push(`Open in Orkestra: ${opts.link}`)

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'PRODID:-//Orkestra//Sales Calendar//EN',
    'VERSION:2.0',
    'METHOD:REQUEST',
    'BEGIN:VEVENT',
    `UID:${m.id}@orkestrapro.com`,
    `DTSTAMP:${toIcsDateUtc(new Date(nowMs).toISOString())}`,
    `DTSTART:${toIcsDateUtc(m.start_time)}`,
    `DTEND:${toIcsDateUtc(m.end_time)}`,
    `SEQUENCE:${seq}`,
    `SUMMARY:${escIcsText(m.title)}`,
  ]
  if (m.location && m.location.trim()) lines.push(`LOCATION:${escIcsText(m.location)}`)
  if (descParts.length) lines.push(`DESCRIPTION:${escIcsText(descParts.join('\n'))}`)
  lines.push(`ORGANIZER;CN=${escIcsText(opts.organizerName)}:mailto:${opts.organizerEmail}`)
  for (const email of (m.attendee_emails ?? [])) {
    lines.push(`ATTENDEE;RSVP=TRUE:mailto:${email}`)
  }
  lines.push('END:VEVENT', 'END:VCALENDAR')

  // CRLF line separators (RFC 5545), folding applied per line, trailing CRLF.
  return lines.map(foldIcsLine).join('\r\n') + '\r\n'
}

// UTF-8-safe base64 for the send-email attachments field (browser btoa is
// latin1-only, so encode to bytes first).
export function icsToBase64(ics: string): string {
  const bytes = new TextEncoder().encode(ics)
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin)
}
